import {
  DocumentNode,
  MutationFunctionOptions,
  MutationHookOptions,
  MutationResult,
  useMutation,
  FetchResult,
} from '@apollo/client'
import ClientLogger from '@eversports/react-components/utilities/client-logger'
import { useEffect } from 'react'

import useCheckoutContext from './useCheckoutContext'

interface ExpectedError {
  id?: string
  path?: string
  message?: string
}
interface ExpectedErrors {
  __typename: 'ExpectedErrors'
  errors?: Array<ExpectedError>
}

interface Data {
  __typename: 'Cart' | 'Order'
}

type CustomMutationResult<M> = Pick<MutationResult<M>, 'data' | 'loading' | 'called'>
type CustomMutation<M, V> = (options?: MutationFunctionOptions<M, V>) => Promise<FetchResult<M>>

const useMutationWithErrorHandling = <M, V>(
  mutation: DocumentNode,
  options: MutationHookOptions<M, V>,
): [CustomMutation<M, V>, CustomMutationResult<M>] => {
  const context = useCheckoutContext()
  const [mutationFunctionRaw, { data, loading, error, called }] = useMutation<M, V>(mutation, {
    ...options,
  })

  const mutationFunction: CustomMutation<M, V> = async (opts) => {
    const result = await mutationFunctionRaw(opts)
    return {
      ...result,
      data: result.data as M, // Cast `MaybeMasked<M>` to `M`
    }
  }

  useEffect(() => {
    if (!error) return

    if (error.networkError) {
      context.setHasMutationNetworkError(true)
    } else {
      throw new Error(`${error.name} - ${error.message}`)
    }
  }, [error])

  useEffect(() => {
    if (!data) return

    const mutationResult = validateMutationData(data)
    const operationName = Object.keys(data)[0]

    if (mutationResult.__typename === 'ExpectedErrors' && !context.getError(operationName)) {
      const message = mutationResult.errors?.[0]?.message
      if (!message) {
        ClientLogger.error('Always query expected errors with all properties "id, path, message" being set')
      }
      context.setError(operationName, message || 'An unexpected error occurred')
    } else if (context.getError(operationName)) {
      context.clearError(operationName)
    }
  }, [data])

  return [mutationFunction, { data, loading, called }]
}

function validateMutationData(data: any): Data | ExpectedErrors {
  const mutationKeys = Object.keys(data)
  if (mutationKeys.length !== 1) {
    throw new Error(`An invalid amount '${mutationKeys.length}' of mutation result has been returned`)
  }
  const mutationName = mutationKeys[0]
  return data[mutationName]
}

export default useMutationWithErrorHandling
